import {
  CREATE_VISIT_SUCCESS,
  GET_VISIT_OPTIONS_SUCCESS,
  JOIN_VISIT_SUCCESS,
  CREATE_VISIT_FAILURE,
  GET_VISIT_OPTIONS_FAILURE,
  JOIN_VISIT_FAILURE,
  JOIN_VISIT_REQUEST,
  GET_VISIT_OPTIONS_REQUEST,
  ACCEPT_VISIT_SUCCESS,
  JOIN_MONITOR_SUCCESS,
  JOIN_MONITOR_FAILURE
} from 'actions/api/visits'
import {
  TWILIO_ROOM_CREATED,
  VISIT_ENDED,
  CLEAR_ERROR,
  SET_VISIT_STATUS,
  SET_WEBSOCKET_CONNECTED,
  VISIT_SUSPENDED,
  VISIT_RESUMED,
  EXTEND_VISIT,
  VISIT_UNCONNECTED,
  HASH_CHANGED,
  BREAK_IN_WARNING_RECEIVED,
  CLEAR_BREAK_IN_WARNING
} from 'actions/visits'
import { keysToCamelCase } from 'utils/objectUtils'
import { isVignetteSupported, VIGNETTE_SUPPORTED_OR_DISABLED } from 'utils/vignetteOSChecker'
import {
  AWAITING_PARTICIPANT,
  CONNECTING,
  VISIT_SUSPENDED_STATUS
} from 'constants/visitStatuses'
import { FLEX10_DEVICE_TYPE, SUSPENDED } from 'constants/visManager'
import { ERROR } from 'constants/unconnectedVisitReasons'

export const initialState = {
  recipientName: null,
  allowedToRecord: null,
  maxDuration: null,
  appointmentId: null,
  userToken: null,
  jwtToken: null,
  webrtcToken: null,
  roomName: null,
  twilioRoomCreatedAt: null,
  inmateDeviceIds: null,
  visitorDeviceIds: null,
  deviceId: null,
  payer: null,
  scheduledEndTime: null,
  visitEnded: false,
  visitEndedDetails: {},
  visitUnconnected: false,
  unconnectedReason: null,
  unconnectedErrorCode: null,
  visitStatus: null,
  inmateId: null,
  visitorId: null,
  participantIds: null,
  monitorJoinError: null,
  isWebSocketConnected: false,
  isFlex10Visit: false,
  isSuspended: false,
  breakInWarningMessages: null
}

export default (state = initialState, action) => {
  switch (action.type) {
    case CREATE_VISIT_SUCCESS: {
      const isSuspended = action.response.status === SUSPENDED
      const visitStatus = isSuspended ? VISIT_SUSPENDED_STATUS : AWAITING_PARTICIPANT

      return {
        ...state,
        ...keysToCamelCase(action.response),
        visitStatus: visitStatus,
        isSuspended: isSuspended,
        participantIds: action.response.participant_ids
      }
    }

    case ACCEPT_VISIT_SUCCESS: {
      const isSuspended = action.response.status === SUSPENDED
      const visitStatus = isSuspended ? VISIT_SUSPENDED_STATUS : (!state.webrtcToken ? AWAITING_PARTICIPANT : null)

      return {
        ...state,
        visitStatus: visitStatus,
        isSuspended: isSuspended,
        participantIds: action.response.participant_ids
      }
    }

    case TWILIO_ROOM_CREATED: {
      return {
        ...state,
        webrtcToken: action.webrtcToken,
        roomName: action.roomName,
        participantIds: action.participantIds,
        twilioRoomCreatedAt: action.serverTimestamp
      }
    }

    case GET_VISIT_OPTIONS_SUCCESS:
    case JOIN_VISIT_SUCCESS: {
      const userToken = action.type === JOIN_VISIT_SUCCESS ? action.response.receiver_token : null
      const vignetteOptions = getVignetteOptions(action)
      const vignetteSupported = isVignetteSupported(vignetteOptions.useVignette, action.response.visit.allowed_to_record)
      if (vignetteSupported !== VIGNETTE_SUPPORTED_OR_DISABLED) {
        return {
          ...state,
          visitUnconnected: true,
          unconnectedReason: ERROR,
          unconnectedErrorCode: vignetteSupported,
          userToken
        }
      }

      const isSuspended = action.response.visit.status === SUSPENDED
      const visitStatus = isSuspended && action.type === JOIN_VISIT_SUCCESS ? VISIT_SUSPENDED_STATUS : null
      const recipientName = action.type === JOIN_VISIT_SUCCESS ? action.response.initiator_name : action.response.recipient_name

      return {
        ...state,
        ...messagingOptions(action),
        ...vignetteOptions,
        allowedToRecord: action.response.visit.allowed_to_record,
        scheduledEndTime: action.response.visit.scheduled_end_time,
        maxDuration: action.response.visit.max_duration,
        isFlex10Visit: action.response.visit.device_type === FLEX10_DEVICE_TYPE,
        recipientName,
        userToken,
        visitStatus,
        isSuspended
      }
    }

    case JOIN_MONITOR_SUCCESS: {
      const isSuspended = action.response.visit.status === SUSPENDED
      const visitStatus = isSuspended ? VISIT_SUSPENDED_STATUS : null

      return {
        ...state,
        webrtcToken: action.response.webrtc_token || { value: action.response.twilio_token, provider: 'twilio' },
        roomName: action.response.room_uid,
        inmateId: action.response.inmate.id,
        visitorId: action.response.visitor.id,
        userToken: action.response.user_token,
        visitStatus: visitStatus,
        isSuspended: isSuspended,
        monitorJoinError: null
      }
    }

    case JOIN_MONITOR_FAILURE: {
      return {
        ...state,
        monitorJoinError: action.error
      }
    }

    case VISIT_ENDED: {
      return {
        ...state,
        visitEnded: true,
        visitEndedDetails: {
          totalMinutes: action.totalMinutes,
          amountCharged: action.amountCharged,
          couponMinutes: action.couponMinutes
        },
        visitStatus: null
      }
    }

    case CREATE_VISIT_FAILURE:
    case JOIN_VISIT_FAILURE:
    case GET_VISIT_OPTIONS_FAILURE: {
      return {
        ...state,
        visitUnconnected: true,
        unconnectedReason: ERROR,
        unconnectedErrorCode: action.error || null
      }
    }

    case CLEAR_ERROR: {
      return {
        ...state,
        visitUnconnected: false,
        unconnectedReason: null,
        unconnectedErrorCode: null
      }
    }

    case VISIT_UNCONNECTED: {
      return {
        ...state,
        visitUnconnected: true,
        unconnectedReason: action.reason,
        unconnectedErrorCode: action.errorCode
      }
    }

    case SET_VISIT_STATUS: {
      return {
        ...state,
        visitStatus: action.status
      }
    }

    case JOIN_VISIT_REQUEST:
    case GET_VISIT_OPTIONS_REQUEST: {
      return {
        ...state,
        visitStatus: CONNECTING
      }
    }

    case VISIT_SUSPENDED: {
      return {
        ...state,
        isSuspended: true
      }
    }

    case VISIT_RESUMED: {
      return {
        ...state,
        isSuspended: false
      }
    }

    case SET_WEBSOCKET_CONNECTED: {
      return {
        ...state,
        isWebSocketConnected: true
      }
    }

    case EXTEND_VISIT: {
      return {
        ...state,
        scheduledEndTime: action.newScheduledEndTime
      }
    }

    case HASH_CHANGED: {
      return {
        ...initialState,
        ...action.visit
      }
    }

    case BREAK_IN_WARNING_RECEIVED: {
      return {
        ...state,
        breakInWarningMessages: state.breakInWarningMessages
          ? [...state.breakInWarningMessages, action.message]
          : [action.message]
      }
    }

    case CLEAR_BREAK_IN_WARNING: {
      return {
        ...state,
        breakInWarningMessages: state.breakInWarningMessages.slice(0, -1)
      }
    }
  }
  return state
}

function messagingOptions(action) {
  const messagingOptions = {}
  const messagingSettings = action.response.messaging_settings
  if (messagingSettings) {
    messagingOptions.afterCallMessagingEnabled = messagingSettings.after_call_message_enabled
  }
  return messagingOptions
}

function getVignetteOptions(action) {
  const vignetteOptions = {}
  const vignetteSettings = action.response.vignette_settings
  if (vignetteSettings) {
    vignetteOptions.useVignette = vignetteSettings.use_visitor_vignette
    vignetteOptions.useBlurredVignette = vignetteSettings.use_visitor_vignette_blurred
  }
  return vignetteOptions
}
